'use client';

import * as React from 'react';
import { type FC, type HTMLAttributes, useEffect, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import type { UseFormReturn } from 'react-hook-form';
import {
    Button,
    buttonVariants,
    Caption,
    Checkbox,
    P,
} from '@paypoteam/ui-components';
import { Tag } from '@phosphor-icons/react';
import parse from 'html-react-parser';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import type { z } from 'zod';

import { formClickEvent } from '@/lib/gtm';
import { cn } from '@/lib/utils';

import { Input } from '@/components/atoms/Input';
import { PhoneNumberInput } from '@/components/atoms/PhoneNumberInput';
import {
    FormControl,
    FormField,
    FormItem,
    FormMessage,
    FormProvider,
} from '@/components/molecules/Form';
import type { MappedConsentType } from '@/components/organisms/RegistrationForm/RegistrationForm.types';
import { handleConsentsState } from '@/components/organisms/RegistrationForm/utils/consentsHelpers';
import type { RegistrationFormSchema } from '@/components/organisms/RegistrationForm/utils/RegistrationFormSchema';
import type { FormConfigType } from '@/components/templates/RegisterFormContent/RegisterFormContent';

import { HANDLE_REGISTRATION_ERROR } from '@/static/content/pl/common';

interface RegistrationFormComponentProps
    extends HTMLAttributes<HTMLFormElement> {
    handleSubmit: (data: z.infer<typeof RegistrationFormSchema>) => void;
    formProviderConfig: UseFormReturn<z.infer<typeof RegistrationFormSchema>>;
    promoCodesConsent: MappedConsentType;
    registrationConsents: MappedConsentType[];
    isLoadingConsents: boolean;
    formName: string;
    formConfig?: FormConfigType[];
    isSubmitButtonSticky?: boolean;
    consentsAvailable: boolean;
}

const RegistrationFormComponent: FC<RegistrationFormComponentProps> = ({
    handleSubmit,
    formProviderConfig,
    isLoadingConsents,
    registrationConsents,
    promoCodesConsent,
    formName,
    children,
    formConfig,
    consentsAvailable,
    isSubmitButtonSticky,
    ...props
}) => {
    const [isPromoCodeFieldVisible, setIsPromoCodeFieldVisible] =
        useState<boolean>(false);
    const [showAgreementsList, setShowAgreementsList] =
        useState<boolean>(false);
    const [formDescription, setFormDescription] =
        useState<FormConfigType | null>();
    const [promoCodesConsentContent, setPromoCodesConsentContent] = useState(
        promoCodesConsent.content,
    );

    const { executeRecaptcha } = useGoogleReCaptcha();

    const searchParams = useSearchParams();

    const discountCodeParam = searchParams.get('discountCode') || '';

    const {
        formState: { isSubmitting, isValid },
        setValue,
        getValues,
        register,
    } = formProviderConfig;

    useEffect(() => {
        if (promoCodesConsent.content) {
            setPromoCodesConsentContent(promoCodesConsent.content);
        }
    }, [promoCodesConsent.content]);

    useEffect(() => {
        setIsPromoCodeFieldVisible(!!formProviderConfig.getValues('userCode'));
    }, [formProviderConfig]);

    useEffect(
        () =>
            setFormDescription(
                formConfig?.find(
                    item =>
                        item.campaignName &&
                        item.campaignName.toUpperCase() ===
                            discountCodeParam.toUpperCase(),
                ),
            ),
        [formConfig, discountCodeParam],
    );

    const onSubmit = async () => {
        if (!executeRecaptcha) {
            throw new Error('Nie udało się załadować tokena reCaptcha');
        }

        const captchaToken = await executeRecaptcha();

        setValue('captcha', captchaToken);

        const values = getValues();

        handleSubmit(values);
    };

    return (
        <FormProvider {...formProviderConfig}>
            <form
                onSubmit={formProviderConfig.handleSubmit(onSubmit)}
                {...props}
            >
                <div
                    className={
                        isSubmitButtonSticky
                            ? 'scrollbar max-h-[380px] overflow-y-auto pr-4'
                            : undefined
                    }
                >
                    <FormField
                        name="email"
                        render={({ field, fieldState }) => (
                            <FormItem className="py-3">
                                <FormControl>
                                    <Input
                                        label="Adres e-mail"
                                        isError={Boolean(fieldState.error)}
                                        autoComplete="email"
                                        data-testid="email-input"
                                        {...field}
                                    />
                                </FormControl>

                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <FormField
                        name="phoneNumber"
                        render={({ field, fieldState }) => (
                            <FormItem className="py-3">
                                <FormControl>
                                    <PhoneNumberInput
                                        label="Numer telefonu"
                                        isError={Boolean(fieldState.error)}
                                        autoComplete="tel"
                                        data-testid="phone-number-input"
                                        {...field}
                                    />
                                </FormControl>

                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    {isPromoCodeFieldVisible ? (
                        <FormField
                            name="userCode"
                            render={({ field, fieldState }) => (
                                <>
                                    <FormItem className="py-3">
                                        <FormControl>
                                            <Input
                                                label="Kod promocyjny"
                                                isError={Boolean(
                                                    fieldState.error,
                                                )}
                                                autoComplete="off"
                                                data-testid="user-code-input"
                                                {...field}
                                            />
                                        </FormControl>

                                        <FormMessage />

                                        {promoCodesConsentContent && (
                                            <Caption
                                                size="3xs"
                                                weight="semi-bold"
                                                className="px-3 text-typo-secondary"
                                            >
                                                {parse(
                                                    promoCodesConsentContent,
                                                )}
                                            </Caption>
                                        )}
                                    </FormItem>
                                </>
                            )}
                        />
                    ) : (
                        <Button
                            type="button"
                            variant="ghost"
                            size="s"
                            disabled={isSubmitting}
                            onClick={() => {
                                setIsPromoCodeFieldVisible(true);
                            }}
                            className="my-3"
                            data-testid="add-promo-code-button"
                        >
                            <Tag
                                size={20}
                                weight="bold"
                                className="text-green-600"
                            />
                            Dodaj kod promocyjny
                        </Button>
                    )}

                    <Caption
                        size="3xs"
                        className="mt-2 text-typo-secondary"
                    >
                        Ta strona jest chroniona przez reCAPTCHA i Google -
                        <Link
                            className={cn(
                                buttonVariants({ variant: 'ghost' }),
                                'ml-1 inline text-3xs font-semibold',
                            )}
                            target="_blank"
                            href="https://policies.google.com/privacy"
                        >
                            Polityka prywatności
                        </Link>
                        .
                    </Caption>

                    <Caption
                        size="3xs"
                        className="!mt-1 text-typo-secondary"
                    >
                        Sprawdź
                        <Link
                            className={cn(
                                buttonVariants({ variant: 'ghost' }),
                                'ml-1 inline text-3xs font-semibold',
                            )}
                            target="_blank"
                            href="https://policies.google.com/terms"
                        >
                            Warunki korzystania z usługi
                        </Link>
                        .
                    </Caption>

                    {consentsAvailable || !isLoadingConsents ? (
                        <>
                            <FormField
                                name="agreementsAcceptance"
                                render={({
                                    field: { value, ...fieldProps },
                                }) => (
                                    <FormItem className="pt-3">
                                        <FormControl>
                                            <Checkbox
                                                {...fieldProps}
                                                checked={value}
                                                onCheckedChange={() => {
                                                    handleConsentsState(
                                                        formProviderConfig,
                                                        registrationConsents,
                                                        'agreementsAcceptance',
                                                        value,
                                                    );

                                                    formClickEvent(
                                                        formName,
                                                        'agreementsAcceptance',
                                                    );
                                                }}
                                                data-testid="agreements-acceptance-checkbox"
                                            >
                                                <>
                                                    <span className="cursor-pointer text-2xs text-typo-secondary">
                                                        Akceptuję wszystkie
                                                        oświadczenia &nbsp;
                                                    </span>

                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="s"
                                                        disabled={
                                                            isLoadingConsents
                                                        }
                                                        className={cn(
                                                            'inline-block w-fit cursor-pointer !text-2xs',
                                                            isLoadingConsents &&
                                                                'cursor-default text-typo-disabled',
                                                        )}
                                                        aria-disabled={
                                                            isLoadingConsents
                                                        }
                                                        onClick={() =>
                                                            setShowAgreementsList(
                                                                !showAgreementsList,
                                                            )
                                                        }
                                                        data-testid="agreements-acceptance-checkbox-explanation-button"
                                                    >
                                                        {showAgreementsList ? (
                                                            <>Zwiń &#9652;</>
                                                        ) : (
                                                            <>Rozwiń &#9662;</>
                                                        )}
                                                    </Button>
                                                </>
                                            </Checkbox>
                                        </FormControl>

                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            {showAgreementsList && (
                                <div className="ml-8">
                                    {registrationConsents.map(
                                        ({
                                            slug,
                                            required,
                                            content,
                                            fieldName,
                                        }) => (
                                            <FormField
                                                key={slug}
                                                name={slug}
                                                render={({
                                                    field: {
                                                        value,
                                                        ...fieldProps
                                                    },
                                                }) => (
                                                    <FormItem className="my-3">
                                                        <FormControl>
                                                            <Checkbox
                                                                {...fieldProps}
                                                                checked={value}
                                                                required={
                                                                    required
                                                                }
                                                                onCheckedChange={() => {
                                                                    handleConsentsState(
                                                                        formProviderConfig,
                                                                        registrationConsents,
                                                                        slug as keyof z.infer<
                                                                            typeof RegistrationFormSchema
                                                                        >,
                                                                        value,
                                                                    );

                                                                    formClickEvent(
                                                                        formName,
                                                                        fieldName,
                                                                    );
                                                                }}
                                                                data-testid={`consents-${slug}-checkbox`}
                                                            >
                                                                <span
                                                                    className="cursor-pointer text-2xs text-typo-secondary [&>a]:font-bold"
                                                                    data-testid={`consents-${slug}-label`}
                                                                >
                                                                    {parse(
                                                                        content,
                                                                    )}
                                                                </span>
                                                            </Checkbox>
                                                        </FormControl>

                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                        ),
                                    )}
                                </div>
                            )}

                            <input
                                {...register('captcha')}
                                className="hidden"
                            />

                            <P
                                className="pt-4 text-typo-secondary"
                                size="2xs"
                                data-testid="personal-data-controller-info"
                            >
                                *Administratorem Twoich danych osobowych jest
                                PayPo sp. z o.o. z siedzibą przy ul.
                                Domaniewskiej 39, 02-672 Warszawa, KRS
                                0000575158, bok@paypo.pl, dalej „PayPo”.
                            </P>
                        </>
                    ) : (
                        <>
                            <P
                                size={'s'}
                                className="text-red-800"
                            >
                                {isSubmitButtonSticky
                                    ? HANDLE_REGISTRATION_ERROR.errorTextModal
                                    : parse(
                                          HANDLE_REGISTRATION_ERROR.errorText,
                                      )}
                            </P>
                        </>
                    )}

                    {children}
                </div>

                {!!formDescription?.text && (
                    <P
                        className="pt-4 text-typo-secondary"
                        size="2xs"
                    >
                        {parse(formDescription.text)}
                    </P>
                )}

                <div
                    className={cn(
                        isSubmitButtonSticky &&
                            'relative w-full bg-background pt-6 before:absolute before:bottom-[100%] before:h-8 before:w-full before:bg-gradient-to-t before:from-white before:to-white/0',
                    )}
                >
                    <Button
                        type="submit"
                        className={cn(
                            'mt-8 w-full',
                            !isSubmitButtonSticky && 'mt-6 md:w-fit',
                        )}
                        disabled={!isValid || isSubmitting}
                        data-testid="registration-submit-button"
                    >
                        Załóż konto PayPo
                    </Button>
                </div>
            </form>
        </FormProvider>
    );
};

export default RegistrationFormComponent;
